<template>
  <div class="flex flex-col gap-4">
    <BusinessSystemAddUsersModal
      ref="businessSystemAddUsersModal"
      :business-system-id="businessSystemId"
      :existing-user-ids="assignedUserIds"
      @added="handleUsersAdded"
    />

    <Card>
      <CardHeader class="flex flex-row items-center justify-between">
        <CardTitle>Users</CardTitle>
        <Button size="sm" @click="openAddUsersModal">
          Add Users
        </Button>
      </CardHeader>
      <CardContent>
        <p v-if="!system || !Array.isArray(system.users)" class="text-sm text-muted-foreground">
          Loading users...
        </p>
        <p v-else-if="systemUsers.length === 0" class="text-sm text-muted-foreground">
          No users are assigned to this business system yet.
        </p>
        <div v-else class="flex flex-col divide-y">
          <div
            v-for="user in systemUsers"
            :key="user.id"
            class="flex items-center justify-between gap-4 py-3"
          >
            <div class="min-w-0">
              <p class="truncate font-medium">
                {{ user.name }}
              </p>
              <p class="truncate text-sm text-muted-foreground">
                {{ user.email }}
              </p>
            </div>

            <div class="flex items-center gap-2">
              <Badge variant="outline">
                {{ resolveRoleName(user?.pivot?.role_id) }}
              </Badge>
              <Button variant="destructive" size="xs" :disabled="store.isLoading" @click="removeUser(user)">
                Remove
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup>
import { inject, computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { Badge } from '../../../../../../../../resources/js/components/ui/badge/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import { useBusinessSystemStore } from '../../../stores/businessSystems.js'
import BusinessSystemAddUsersModal from '../../../components/business-systems/BusinessSystemAddUsersModal.vue'

const system = inject('businessSystem', ref(null))
const store = useBusinessSystemStore()
const businessSystemAddUsersModal = ref(null)
const roleNameById = ref({})

const businessSystemId = computed(() => Number(system.value?.id ?? 0))
const systemUsers = computed(() => {
  const users = system.value?.users

  if (!Array.isArray(users)) {
    return []
  }

  return [...users].sort((firstUser, secondUser) => {
    return String(firstUser.name ?? '').localeCompare(String(secondUser.name ?? ''))
  })
})
const assignedUserIds = computed(() => {
  return systemUsers.value.map((user) => Number(user.id))
})

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  return []
}

const fetchRoleNames = async () => {
  const { data } = await axios.get('/api/v1/user-roles', {
    params: {
      not_paginated: true,
    },
  })
  const roles = normalizeCollection(data)
  roleNameById.value = roles.reduce((accumulator, role) => {
    accumulator[Number(role.id)] = role.name

    return accumulator
  }, {})
}

const resolveRoleName = (roleId) => {
  const normalizedRoleId = Number(roleId)

  if (!Number.isFinite(normalizedRoleId) || normalizedRoleId <= 0) {
    return 'Unknown role'
  }

  return roleNameById.value[normalizedRoleId] ?? `Role #${normalizedRoleId}`
}

const openAddUsersModal = () => {
  businessSystemAddUsersModal.value?.openModal()
}

const handleUsersAdded = async () => {
  await store.fetchItemById(businessSystemId.value)
}

const removeUser = async (user) => {
  if (!confirm(`Remove ${user.name} from this business system?`)) {
    return
  }

  await store.removeUserFromBusinessSystem(businessSystemId.value, user.id)
  toast.success('User removed from business system.')
}

onMounted(async () => {
  await fetchRoleNames()
})
</script>
